import { useState, useRef, useEffect, useCallback } from 'react'
import { apiErrorMessage, displayName, findMyLayout, type LayoutSummary } from '../../lib/dashboardLayouts'
import type { DashboardType } from '../../stores/layoutActions'
import { ConfirmDialog, PromptDialog } from '../ConfirmDialog'

interface LayoutPresetsMenuProps {
    /** The user's own saved layouts for this dashboard */
    layouts: LayoutSummary[]
    /** The personal layout on screen; null while the system default is showing */
    currentLayoutId: string | null
    currentLayoutName: string | null
    isEditing: boolean
    hasUnsavedChanges: boolean
    dashboardType: DashboardType
    /** Called as the menu opens, to refresh the list */
    onOpen: () => void
    onSelectLayout: (id: string) => void
    onExport: () => string
    onImport: (json: string) => string | null  // returns error string or null
    onSaveFirst: () => void
    onRevert: () => void
    onRename: (id: string, name: string) => Promise<void>
    onDelete: (id: string) => Promise<void>
    onMakeDefault: (id: string) => Promise<void>
}

/**
 * The Layouts menu in the edit toolbar: My layout (the user's default) and the system default,
 * then any named layouts — each with rename, make-default and delete — then Revert, Import and
 * Export. The button names the layout on screen.
 */
export default function LayoutPresetsMenu({
    layouts, currentLayoutId, currentLayoutName, isEditing, hasUnsavedChanges, dashboardType,
    onOpen, onSelectLayout, onExport, onImport, onSaveFirst, onRevert, onRename, onDelete, onMakeDefault,
}: LayoutPresetsMenuProps) {
    const [open, setOpen] = useState(false)
    const [modalMode, setModalMode] = useState<'import' | 'export' | null>(null)
    const [modalJson, setModalJson] = useState('')
    const [importError, setImportError] = useState<string | null>(null)
    const [importSuccess, setImportSuccess] = useState(false)
    const [copied, setCopied] = useState(false)
    const [pendingAction, setPendingAction] = useState<{ type: 'switch'; layoutId: string } | { type: 'import'; json: string } | null>(null)
    const [unsavedPrompt, setUnsavedPrompt] = useState(false)
    const [renaming, setRenaming] = useState<LayoutSummary | null>(null)
    const [deleting, setDeleting] = useState<LayoutSummary | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    // Close popover on outside click (a menu, so exempt from the no-backdrop-dismiss modal rule)
    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    const mine = findMyLayout(layouts)
    const named = layouts.filter(l => !l.isDefault)
    const onSystemDefault = !currentLayoutId
    const buttonLabel = currentLayoutId ? (currentLayoutName?.trim() || 'My layout') : 'System default'

    const toggle = () => {
        if (!open) onOpen()
        setOpen(!open)
    }

    const run = async (action: () => Promise<void>) => {
        try {
            await action()
        } catch (e) {
            setActionError(apiErrorMessage(e, 'Your layouts could not be updated. Please try again.'))
        }
    }

    // ── Layout selection with unsaved-changes guard ──────────────────
    const handleSelectLayout = useCallback((layoutId: string) => {
        setOpen(false)
        if (hasUnsavedChanges && isEditing) {
            setPendingAction({ type: 'switch', layoutId })
            setUnsavedPrompt(true)
        } else {
            onSelectLayout(layoutId)
        }
    }, [hasUnsavedChanges, isEditing, onSelectLayout])

    // ── Unsaved changes prompt actions ───────────────────────────────
    const handleUnsavedSave = () => {
        setUnsavedPrompt(false)
        // Saving ends edit mode; the user re-picks the layout they were switching to afterwards
        onSaveFirst()
        setPendingAction(null)
    }

    const handleUnsavedDiscard = () => {
        setUnsavedPrompt(false)
        if (pendingAction?.type === 'switch') {
            onSelectLayout(pendingAction.layoutId)
        } else if (pendingAction?.type === 'import') {
            const err = onImport(pendingAction.json)
            if (err) {
                setImportError(err)
                setModalMode('import')
                setModalJson(pendingAction.json)
            }
        }
        setPendingAction(null)
    }

    const handleUnsavedCancel = () => {
        setUnsavedPrompt(false)
        setPendingAction(null)
    }

    // ── Export ───────────────────────────────────────────────────────
    const handleOpenExport = () => {
        setOpen(false)
        const json = onExport()
        setModalJson(json)
        setModalMode('export')
        setImportError(null)
        setImportSuccess(false)
        setCopied(false)
    }

    const handleCopyJson = async () => {
        try {
            await navigator.clipboard.writeText(modalJson)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // Fallback: select text
            const textarea = document.querySelector('.layout-ie-textarea') as HTMLTextAreaElement
            if (textarea) { textarea.select(); document.execCommand('copy') }
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    // ── Import ──────────────────────────────────────────────────────
    const handleOpenImport = () => {
        setOpen(false)
        setModalJson('')
        setModalMode('import')
        setImportError(null)
        setImportSuccess(false)
    }

    const handleImportSubmit = () => {
        setImportError(null)
        setImportSuccess(false)

        if (!modalJson.trim()) {
            setImportError('Please paste a layout JSON string.')
            return
        }

        if (hasUnsavedChanges && isEditing) {
            setPendingAction({ type: 'import', json: modalJson })
            setUnsavedPrompt(true)
            setModalMode(null)
            return
        }

        const err = onImport(modalJson)
        if (err) {
            setImportError(err)
        } else {
            setImportSuccess(true)
            setTimeout(() => setModalMode(null), 800)
        }
    }

    // ── A saved layout's row ─────────────────────────────────────────
    const renderRow = (l: LayoutSummary) => {
        const active = currentLayoutId === l.id
        const name = displayName(l)
        return (
            <div key={l.id} className="layout-presets-row">
                <button className={`layout-presets-item${active ? ' active' : ''}`} onClick={() => handleSelectLayout(l.id)}>
                    <span className="presets-check">{active ? '✓' : ''}</span>
                    <span className="presets-label">
                        {name}
                        {l.isDefault && <span className="presets-badge">Default</span>}
                    </span>
                </button>
                <span className="presets-actions">
                    <button className="presets-action" title="Rename" aria-label={`Rename ${name}`}
                        onClick={() => { setOpen(false); setRenaming(l) }}>✎</button>
                    {!l.isDefault && (
                        <>
                            <button className="presets-action" title="Make this my default" aria-label={`Make ${name} my default`}
                                onClick={() => run(() => onMakeDefault(l.id))}>★</button>
                            <button className="presets-action danger" title="Delete" aria-label={`Delete ${name}`}
                                onClick={() => { setOpen(false); setDeleting(l) }}>🗑</button>
                        </>
                    )}
                </span>
            </div>
        )
    }

    // ── Render ───────────────────────────────────────────────────────
    return (
        <>
            {/* Layouts Button */}
            <div ref={menuRef} style={{ position: 'relative' }}>
                <button className="edit-mode-btn" onClick={toggle} aria-haspopup="menu" aria-expanded={open}>
                    📋 {buttonLabel} ▾
                </button>

                {/* Popover */}
                {open && (
                    <div className="layout-presets-popover">
                        {mine && renderRow(mine)}

                        {/* The system default — the user's default until they save their own */}
                        <button
                            className={`layout-presets-item${onSystemDefault ? ' active' : ''}`}
                            onClick={() => handleSelectLayout('system-default')}
                        >
                            <span className="presets-check">{onSystemDefault ? '✓' : ''}</span>
                            <span className="presets-label">
                                System default
                                {!mine && <span className="presets-badge">Default</span>}
                            </span>
                        </button>

                        {named.length > 0 && (
                            <>
                                <div className="layout-presets-heading">Saved layouts</div>
                                {named.map(renderRow)}
                            </>
                        )}

                        {/* Divider */}
                        <div className="layout-presets-divider" />

                        {mine && (
                            <button className="layout-presets-item danger" onClick={() => { setOpen(false); onRevert() }}>
                                <span className="presets-check">↺</span>
                                <span className="presets-label">Revert to system default</span>
                            </button>
                        )}

                        {/* Import / Export */}
                        <button className="layout-presets-item" onClick={handleOpenImport}>
                            <span className="presets-check" style={{ fontSize: '13px' }}>📥</span>
                            <span className="presets-label">Import Layout…</span>
                        </button>
                        <button className="layout-presets-item" onClick={handleOpenExport}>
                            <span className="presets-check" style={{ fontSize: '13px' }}>📤</span>
                            <span className="presets-label">Export Layout</span>
                        </button>
                    </div>
                )}
            </div>

            {/* ── Import / Export Modal ──────────────────────────────── */}
            {modalMode && (
                <div className="layout-ie-overlay">
                    <div className="layout-ie-modal">
                        <h3>{modalMode === 'export' ? 'Export Layout' : 'Import Layout'}</h3>

                        {modalMode === 'export' && (
                            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', margin: '0 0 var(--space-3)' }}>
                                Copy this JSON to save or share your current {dashboardType} dashboard layout.
                            </p>
                        )}

                        {modalMode === 'import' && (
                            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', margin: '0 0 var(--space-3)' }}>
                                Paste a previously exported {dashboardType} dashboard layout JSON below.
                            </p>
                        )}

                        <textarea
                            className="layout-ie-textarea"
                            value={modalJson}
                            onChange={e => { setModalJson(e.target.value); setImportError(null); setImportSuccess(false) }}
                            readOnly={modalMode === 'export'}
                            placeholder={modalMode === 'import' ? 'Paste layout JSON here…' : ''}
                            spellCheck={false}
                            rows={12}
                            autoFocus={modalMode === 'import'}
                        />

                        {importError && (
                            <div className="layout-ie-error">{importError}</div>
                        )}

                        {importSuccess && (
                            <div className="layout-ie-success">✓ Layout imported successfully!</div>
                        )}

                        <div className="layout-ie-actions">
                            {modalMode === 'export' ? (
                                <>
                                    <button className="btn btn-primary" onClick={handleCopyJson}>
                                        {copied ? '✓ Copied!' : 'Copy to Clipboard'}
                                    </button>
                                    <button className="btn btn-secondary" onClick={() => setModalMode(null)}>Close</button>
                                </>
                            ) : (
                                <>
                                    <button className="btn btn-primary" onClick={handleImportSubmit} disabled={!modalJson.trim()}>
                                        Import
                                    </button>
                                    <button className="btn btn-secondary" onClick={() => setModalMode(null)}>Cancel</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Unsaved Changes Prompt ─────────────────────────────── */}
            {unsavedPrompt && (
                <div className="layout-ie-overlay">
                    <div className="layout-ie-modal" style={{ maxWidth: '380px' }}>
                        <h3>Unsaved Changes</h3>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 var(--space-5)' }}>
                            You have unsaved layout changes. Would you like to save them first?
                        </p>
                        <div className="layout-ie-actions">
                            <button className="btn btn-primary" onClick={handleUnsavedSave}>
                                💾 Save
                            </button>
                            <button className="btn btn-secondary" onClick={handleUnsavedDiscard}
                                style={{ borderColor: 'var(--color-warning)', color: 'var(--color-warning)' }}>
                                Discard
                            </button>
                            <button className="btn btn-secondary" onClick={handleUnsavedCancel}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Rename / delete ────────────────────────────────────── */}
            {renaming && (
                <PromptDialog
                    title="Rename layout"
                    label="Name"
                    defaultValue={displayName(renaming)}
                    confirmLabel="Rename"
                    onConfirm={name => {
                        const id = renaming.id
                        setRenaming(null)
                        run(() => onRename(id, name))
                    }}
                    onCancel={() => setRenaming(null)}
                />
            )}
            {deleting && (
                <ConfirmDialog
                    title={`Delete “${displayName(deleting)}”?`}
                    message={currentLayoutId === deleting.id
                        ? 'This removes the saved layout for good. It’s the one on screen, so the dashboard switches to your default.'
                        : 'This removes the saved layout for good.'}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => {
                        const id = deleting.id
                        setDeleting(null)
                        run(() => onDelete(id))
                    }}
                    onCancel={() => setDeleting(null)}
                />
            )}
            {actionError && (
                <ConfirmDialog
                    title="Your layouts weren’t changed"
                    message={actionError}
                    confirmLabel="OK"
                    onConfirm={() => setActionError(null)}
                    onCancel={() => setActionError(null)}
                />
            )}
        </>
    )
}
